/**
 * iter-128b — Central API client.
 *
 * Prefers **same-origin** (`window.location.origin`) so Cloudflare /
 * ingress can't strip the `Authorization` header on cross-origin
 * requests (this bit us in iter-127e for the FB marketing module).
 * Falls back to `REACT_APP_BACKEND_URL` for tests and SSR.
 *
 * Every non-public feature file should import from here:
 *
 *   import { apiClient, API, BACKEND_URL } from "../lib/api";
 *
 * Do NOT re-instantiate `axios.create({ baseURL: ... })` elsewhere —
 * the interceptor below handles auth, 401 logout, and the 402
 * subscription-gate redirect for all callers.
 */
import axios from "axios";

const _envBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const BACKEND_URL =
  (typeof window !== "undefined" && window.location && window.location.origin)
    ? window.location.origin
    : _envBase;

export const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

// Attach the JWT (if any) to every outbound request.
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global response handling: 401 → logout, 402 subscription gate → toast + redirect.
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error?.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    }
    if (
      error?.response?.status === 402 &&
      error?.response?.data?.code === "subscription_required"
    ) {
      if (!window.__subGateToastShown) {
        window.__subGateToastShown = true;
        setTimeout(() => { window.__subGateToastShown = false; }, 8000);
        try {
          import("sonner").then(({ toast }) => {
            toast.error(
              error.response?.data?.detail ||
              "Your band's free trial has ended — choose a plan to keep editing."
            );
          });
        } catch (_) { /* noop */ }
        if (!window.location.pathname.startsWith("/subscription")) {
          setTimeout(() => { window.location.href = "/subscription"; }, 1200);
        }
      }
    }
    // iter-135 — band was suspended by the platform administrator.
    // Bounce the user to a dedicated /suspended screen with the
    // reason message so they don't keep hitting silent 402s.
    if (
      error?.response?.status === 402 &&
      error?.response?.data?.code === "band_suspended"
    ) {
      if (!window.__suspendGateShown) {
        window.__suspendGateShown = true;
        setTimeout(() => { window.__suspendGateShown = false; }, 8000);
        try {
          import("sonner").then(({ toast }) => {
            toast.error(
              error.response?.data?.detail ||
              "This band has been suspended. Please contact support."
            );
          });
        } catch (_) { /* noop */ }
        if (!window.location.pathname.startsWith("/suspended")) {
          try {
            sessionStorage.setItem(
              "vivo-suspend-reason",
              JSON.stringify(error.response?.data || {}),
            );
          } catch (_) { /* noop */ }
          setTimeout(() => { window.location.href = "/suspended"; }, 800);
        }
      }
    }
    return Promise.reject(error);
  },
);

export default apiClient;
